// src/utils/auth.js
// ══════════════════════════════════════════════════════════════════════════════════════
// Enterprise Authentication & Session Management Utility
// ══════════════════════════════════════════════════════════════════════════════════════
// Features:
// - Universal Token Resolution (Fixes Axios 401 Interceptor sync issues)
// - Proactive Background Token Rotation
// - Cross-Tab Session Synchronization
// - Idle Inactivity Timeout Enforcement
// - Refresh Token Concurrency Locks (Mutex)
// - Role-Based Access Control (RBAC) Helpers
// ══════════════════════════════════════════════════════════════════════════════════════

import { jwtDecode } from 'jwt-decode';
import { config } from '../config';

// ============================================================================
// CONFIGURATION & CONSTANTS
// ============================================================================

const CONFIG = {
  // Token Keys - Ordered by priority for the universal resolver
  TOKEN_KEYS: ['authToken', 'jwt_token', 'token', 'access_token'],
  REFRESH_TOKEN_KEYS: ['refreshToken', 'refresh_token'],
  USER_INFO_KEY: 'user',
  
  // Session parameters
  PROACTIVE_REFRESH_LEAD_TIME_MS: 60000, // Refresh 1 minute before expiry
  IDLE_TIMEOUT_MS: 30 * 60 * 1000,       // 30 minutes of inactivity = logout
  DEBUG_MODE: process.env.NODE_ENV !== 'production',
  
  // Endpoints
  REFRESH_ENDPOINT: `${config.apiUrl.replace(/\/api\/v1$/, '')}/api/v1/auth/refresh`,
};

// Internal State
let refreshPromise = null;      // Concurrency lock for refresh requests
let proactiveRefreshTimer = null; // Background rotation timer
let idleTimer = null;           // Inactivity timer

// ============================================================================
// INTERNAL LOGGER
// ============================================================================

const log = (level, message, data = null) => {
  if (!CONFIG.DEBUG_MODE && level !== 'error' && level !== 'warn') return;
  const timestamp = new Date().toISOString();
  const consoleMethod = console[level] || console.log;
  const prefix = `[${timestamp}] [AuthManager] [${level.toUpperCase()}]`;
  if (data) {
    consoleMethod(`${prefix} ${message}`, data);
  } else {
    consoleMethod(`${prefix} ${message}`);
  }
};

// ============================================================================
// STORAGE ABSTRACTION & UNIVERSAL RESOLVERS
// ============================================================================
// Fixes the issue where different services look for different token names.

/**
 * Intelligently hunts for an access token across all common keys and storage mechanisms.
 * @returns {string|null} The resolved token
 */
const resolveUniversalToken = () => {
  for (const key of CONFIG.TOKEN_KEYS) {
    const sessionToken = sessionStorage.getItem(key);
    if (sessionToken) return sessionToken;
    
    const localToken = localStorage.getItem(key);
    if (localToken) return localToken;
  }
  return null;
};

/**
 * Ensures the token is synced across all storage mechanisms that services might check.
 */
const syncTokenToAllMechanisms = (token) => {
  if (!token) return;
  // Write to the primary key
  sessionStorage.setItem(CONFIG.TOKEN_KEYS[0], token);
  // Write to legacy/interceptor keys to guarantee compatibility with aiInsightsService
  localStorage.setItem('jwt_token', token);
  localStorage.setItem('token', token);
};

/**
 * Wipes all possible token keys from all storage locations.
 */
const purgeAllTokenMechanisms = () => {
  CONFIG.TOKEN_KEYS.forEach(key => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
  CONFIG.REFRESH_TOKEN_KEYS.forEach(key => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
  sessionStorage.removeItem(CONFIG.USER_INFO_KEY);
  localStorage.removeItem(CONFIG.USER_INFO_KEY);
};

// ============================================================================
// CORE AUTHENTICATION EXPORTS (Backwards Compatible)
// ============================================================================

/**
 * Stores the authentication token securely and initiates background rotation.
 * @param {string} token - The JWT received from the backend.
 * @param {boolean} rememberMe - If true, persists to localStorage.
 */
export const storeAuthToken = (token, rememberMe = false) => {
  if (!token) {
    log('warn', 'Attempted to store null or undefined token.');
    return;
  }

  try {
    // Validate token format before storing
    const decoded = jwtDecode(token);
    
    // Sync across mechanisms to fix the 401 Interceptor bug
    syncTokenToAllMechanisms(token);
    
    if (rememberMe) {
      localStorage.setItem(CONFIG.TOKEN_KEYS[0], token);
    }
    
    log('info', 'Auth token successfully stored and synced.');
    
    // Dispatch system-wide login event
    window.dispatchEvent(new CustomEvent('auth:login', { detail: { user: decoded } }));
    
    // Restart proactive timers
    manageProactiveRefresh();
    startIdleTimer();
    
  } catch (error) {
    log('error', 'Attempted to store a malformed JWT token', error);
  }
};

/**
 * Retrieves the currently active authentication token.
 * Uses the universal resolver to find it regardless of where other scripts saved it.
 * @returns {string | null}
 */
export const getAuthToken = () => {
  return resolveUniversalToken();
};

/**
 * Stores the refresh token securely.
 * @param {string} token 
 * @param {boolean} rememberMe 
 */
export const storeRefreshToken = (token, rememberMe = false) => {
  if (!token) return;
  sessionStorage.setItem(CONFIG.REFRESH_TOKEN_KEYS[0], token);
  if (rememberMe) {
    localStorage.setItem(CONFIG.REFRESH_TOKEN_KEYS[0], token);
  }
};

/**
 * Retrieves the refresh token.
 * @returns {string|null}
 */
export const getRefreshToken = () => {
  for (const key of CONFIG.REFRESH_TOKEN_KEYS) {
    const sessionToken = sessionStorage.getItem(key);
    if (sessionToken) return sessionToken;
    
    const localToken = localStorage.getItem(key);
    if (localToken) return localToken;
  }
  return null;
};

/**
 * Stores user information in sessionStorage.
 * @param {object} user - The user object received from the backend.
 */
export const storeUserInfo = (user) => {
  if (user && typeof user === 'object') {
    const stringified = JSON.stringify(user);
    sessionStorage.setItem(CONFIG.USER_INFO_KEY, stringified);
    localStorage.setItem(CONFIG.USER_INFO_KEY, stringified); // Sync for cross-tab
    log('info', 'User info stored', user.id || user.email || 'Unknown User');
  } else {
    log('warn', 'Attempted to store invalid user data.', user);
  }
};

/**
 * Retrieves user information from storage.
 * @returns {object | null}
 */
export const getUserInfo = () => {
  const userData = sessionStorage.getItem(CONFIG.USER_INFO_KEY) || localStorage.getItem(CONFIG.USER_INFO_KEY);
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch (e) {
      log('error', 'Failed to parse user data from storage. Purging corrupted data.', e);
      sessionStorage.removeItem(CONFIG.USER_INFO_KEY);
      localStorage.removeItem(CONFIG.USER_INFO_KEY);
      return null;
    }
  }
  return null;
};

/**
 * Completely terminates the user session, clears timers, and purges all data.
 * Broadcasts an event so the React UI can update immediately.
 * @param {boolean} skipEvent - Internal flag to prevent infinite loops
 */
export const clearAuthData = (skipEvent = false) => {
  purgeAllTokenMechanisms();
  
  // Stop background workers
  stopProactiveRefresh();
  stopIdleTimer();
  
  log('info', 'All authentication data and timers cleared.');
  
  if (!skipEvent) {
    // Notify the rest of the application (e.g., App.js routers, useAIInsights hook)
    window.dispatchEvent(new CustomEvent('auth:logout'));
    
    // Broadcast to other open browser tabs
    localStorage.setItem('auth_sync_logout', Date.now().toString());
  }
};

// ============================================================================
// TOKEN VALIDATION & DECODING
// ============================================================================

/**
 * Safely decodes the current access token.
 * @returns {object|null} Decoded payload or null if invalid
 */
export const getDecodedToken = () => {
  const token = getAuthToken();
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch (error) {
    log('error', 'Failed to decode token', error);
    return null;
  }
};

/**
 * Retrieves the remaining time on the token in milliseconds.
 * @returns {number} MS until expiration, or 0 if expired/invalid.
 */
export const getTokenRemainingTimeMs = () => {
  const decoded = getDecodedToken();
  if (!decoded || !decoded.exp) return 0;
  
  const currentTimeMs = Date.now();
  const expTimeMs = decoded.exp * 1000;
  const timeRemaining = expTimeMs - currentTimeMs;
  
  return timeRemaining > 0 ? timeRemaining : 0;
};

/**
 * Checks if a user is currently authenticated AND if the token is not expired.
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const timeRemaining = getTokenRemainingTimeMs();
  
  if (timeRemaining <= 0) {
    const tokenExists = !!getAuthToken();
    if (tokenExists) {
      log('warn', 'Token exists but is mathematically expired.');
      // Don't auto-clear here yet, let the interceptor or proactive refresh handle it first
      // unless it's severely expired.
    }
    return false;
  }
  
  return true;
};

// ============================================================================
// ADVANCED REFRESH & CONCURRENCY MANAGEMENT (MUTEX)
// ============================================================================

/**
 * Attempts to refresh the access token using the refresh token.
 * Incorporates a Promise Lock (Mutex) to ensure that if 10 API calls fail 
 * simultaneously, only ONE refresh request is sent to the backend.
 * @returns {Promise<string|null>} The new access token or null if failed.
 */
export const refreshAuthSession = async () => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    log('warn', 'Refresh attempted but no refresh token is present in storage.');
    clearAuthData();
    return null;
  }

  // CONCURRENCY LOCK: If a refresh is already in flight, wait for it instead of firing another.
  if (refreshPromise) {
    log('info', 'Refresh already in progress. Waiting on existing lock...');
    return refreshPromise;
  }

  log('info', 'Initiating token refresh request to backend...');

  // Create the promise and lock it
  refreshPromise = (async () => {
    try {
      const response = await fetch(CONFIG.REFRESH_ENDPOINT, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      if (response.ok) {
        const data = await response.json();
        
        const newAccessToken = data.accessToken || data.token;
        const newRefreshToken = data.refreshToken || data.refresh_token;

        if (newAccessToken) {
          log('info', 'Token refresh successful.');
          storeAuthToken(newAccessToken);
          
          if (newRefreshToken) {
            storeRefreshToken(newRefreshToken);
          }
          
          window.dispatchEvent(new CustomEvent('auth:refresh_success'));
          return newAccessToken;
        }
      } 
      
      // If we reach here, the response was not OK or missing the token
      log('warn', `Refresh rejected by server. Status: ${response.status}`);
      throw new Error('Server rejected refresh token');

    } catch (error) {
      log('error', 'Network or server error during token refresh.', error);
      
      // Security: If the refresh token is dead, the session is dead.
      clearAuthData();
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      return null;
      
    } finally {
      // RELEASE THE LOCK
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Proactively schedules a refresh right before the token expires.
 * This prevents users from experiencing mid-interaction API failures.
 */
const manageProactiveRefresh = () => {
  stopProactiveRefresh();
  
  const timeRemainingMs = getTokenRemainingTimeMs();
  if (timeRemainingMs <= 0) return;

  // Calculate when to trigger the refresh
  const timeUntilRefreshMs = timeRemainingMs - CONFIG.PROACTIVE_REFRESH_LEAD_TIME_MS;
  
  if (timeUntilRefreshMs <= 0) {
    // Token is expiring in less than a minute. Refresh immediately.
    log('info', 'Token is close to expiry. Proactive refresh triggered immediately.');
    refreshAuthSession();
  } else {
    // Schedule the refresh
    log('info', `Proactive background refresh scheduled in ${Math.round(timeUntilRefreshMs / 1000 / 60)} minutes.`);
    proactiveRefreshTimer = setTimeout(() => {
      log('info', 'Proactive timer executed. Refreshing session...');
      refreshAuthSession();
    }, timeUntilRefreshMs);
  }
};

const stopProactiveRefresh = () => {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
};

// ============================================================================
// IDLE SESSION MANAGEMENT
// ============================================================================
// Protects enterprise data if a user walks away from a public/shared terminal.

const resetIdleTimer = () => {
  // Only track idle time if they are actually logged in
  if (!isAuthenticated()) return;

  stopIdleTimer();
  
  idleTimer = setTimeout(() => {
    log('warn', `User idle for ${CONFIG.IDLE_TIMEOUT_MS / 1000 / 60} minutes. Locking session.`);
    clearAuthData();
    window.dispatchEvent(new CustomEvent('auth:idle_timeout'));
  }, CONFIG.IDLE_TIMEOUT_MS);
};

const startIdleTimer = () => {
  if (typeof window === 'undefined') return;
  
  // Attach activity listeners
  window.addEventListener('mousemove', resetIdleTimer, { passive: true });
  window.addEventListener('keypress', resetIdleTimer, { passive: true });
  window.addEventListener('click', resetIdleTimer, { passive: true });
  window.addEventListener('scroll', resetIdleTimer, { passive: true });
  
  resetIdleTimer();
};

const stopIdleTimer = () => {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  
  if (typeof window === 'undefined') return;
  window.removeEventListener('mousemove', resetIdleTimer);
  window.removeEventListener('keypress', resetIdleTimer);
  window.removeEventListener('click', resetIdleTimer);
  window.removeEventListener('scroll', resetIdleTimer);
};

// ============================================================================
// CROSS-TAB SYNCHRONIZATION
// ============================================================================
// Ensures that if a user logs out in Tab A, Tab B securely locks immediately.

const handleStorageEvents = (event) => {
  if (event.key === 'auth_sync_logout') {
    log('info', 'Cross-tab logout event detected. Terminating local session.');
    clearAuthData(true); // skip emitting the broadcast to avoid infinite loops
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', handleStorageEvents);
}

// ============================================================================
// SYSTEM EVENT LISTENERS (Binding to external services like Axios)
// ============================================================================

if (typeof window !== 'undefined') {
  // Listen for the Axios interceptor firing a 401
  window.addEventListener('auth:unauthorized', async () => {
    log('warn', 'System detected an unauthorized (401) API event.');
    
    // Attempt a final "Hail Mary" refresh before kicking the user to login
    const token = await refreshAuthSession();
    if (!token) {
      log('error', 'Hail Mary refresh failed. Terminating session entirely.');
      clearAuthData();
    }
  });
}

// ============================================================================
// ROLE-BASED ACCESS CONTROL (RBAC) HELPERS
// ============================================================================

/**
 * Checks if the currently authenticated user possesses a specific role.
 * @param {string|string[]} expectedRoles - The role(s) to check against.
 * @returns {boolean}
 */
export const hasRole = (expectedRoles) => {
  const user = getUserInfo();
  if (!user || !user.role) return false;

  const rolesToCheck = Array.isArray(expectedRoles) ? expectedRoles : [expectedRoles];
  const userRoles = Array.isArray(user.role) ? user.role : [user.role];

  // Return true if the user has ANY of the expected roles
  return rolesToCheck.some(role => userRoles.includes(role));
};

/**
 * Checks if the user possesses a specific fine-grained permission.
 * Assumes the JWT payload or user object contains a `permissions` array.
 * @param {string} permission - e.g., 'dispatch:auto_assign'
 * @returns {boolean}
 */
export const hasPermission = (permission) => {
  const decoded = getDecodedToken();
  const user = getUserInfo();
  
  // Check token payload first (most secure), then fallback to local user object
  const permissions = (decoded && decoded.permissions) || (user && user.permissions) || [];
  
  return Array.isArray(permissions) && permissions.includes(permission);
};

/**
 * Bootstraps the authentication manager on app load.
 * Checks for existing sessions and restarts background tasks.
 */
export const initializeAuthManager = () => {
  log('info', 'Initializing Enterprise Auth Manager...');
  if (isAuthenticated()) {
    log('info', 'Active session detected on boot. Resuming background tasks.');
    manageProactiveRefresh();
    startIdleTimer();
  } else {
    log('info', 'No active session found on boot.');
    // Clean up any fragmented legacy data just in case
    purgeAllTokenMechanisms(); 
  }
};

// Auto-run bootstrap on module load
initializeAuthManager();